import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { KEYCLOAK_JSON, WEB_ENDPOINT } from '../utils/env';

// keycloak-js 26.x is a native ES module and returns real Promises. The 3.4.3 it replaced
// was a 2017 UMD build with a bespoke `.success()/.error()` thenable, and it could not talk
// to Keycloak 26 at all: it required the `nonce` claim in the access and refresh tokens,
// which Keycloak stopped emitting in 25 because OIDC only defines it for the ID token. The
// result was "[KEYCLOAK] Invalid nonce, clearing token" and an endless redirect loop.
import Keycloak from 'keycloak-js';

@Injectable({ providedIn: 'root' })
export class KeycloakService {
  static auth: any = {};
  static logoutFuncs: any[] = [];
  static updateRefreshTokenFunc: any;
  static i18nAcceptLanguageHeader: string;

  /**
   * Realm roles the platform knows. Keycloak 26 adds default-roles-<realm>, offline_access
   * and uma_authorization to every token (C2027-182); they must never drive a UI decision,
   * so nothing here may depend on the raw role count or on the order of realm_access.roles.
   */
  static readonly PLATFORM_ROLES = [
    'ROLE_ADMIN',
    'ROLE_SECTOR',
    'ROLE_NEGOCE',
    'ROLE_MANAGER',
    'ROLE_ADVISOR',
    'ROLE_FARMER',
  ];

  /** Platform roles carried by the current token, in PLATFORM_ROLES order. */
  public static platformRoles(): string[] {
    const roles: string[] = KeycloakService.auth.authz?.tokenParsed?.realm_access?.roles ?? [];
    return KeycloakService.PLATFORM_ROLES.filter((role) => roles.includes(role));
  }

  /** True when the token carries ROLE_FARMER and no other platform role. */
  public static isFarmerOnly(): boolean {
    const roles = KeycloakService.platformRoles();
    return roles.length === 1 && roles[0] === 'ROLE_FARMER';
  }

  static init(token?: string, refreshToken?: string, idToken?: string): Observable<any> {
    const keycloakAuth: any = new (Keycloak as any)(KEYCLOAK_JSON);
    KeycloakService.auth.loggedIn = false;
    let initParams: any;
    if (!token) {
      initParams = {
        onLoad: 'login-required',
      };
    } else {
      initParams = {
        checkLoginIframe: false,
        token: token,
        refreshToken: refreshToken,
        idToken: idToken,
      };
    }

    return new Observable((subscriber: Subscriber<any>) => {
      keycloakAuth
        .init(initParams)
        .then(() => {
          KeycloakService.auth.loggedIn = true;
          KeycloakService.auth.authz = keycloakAuth;
          subscriber.next(true);
          subscriber.complete();
        })
        .catch(() => {
          subscriber.error(null);
        });
    });
  }

  public static registerUpdateRefreshTokenFunc(func: any) {
    KeycloakService.updateRefreshTokenFunc = func;
  }

  /**
   * Refresh the Keycloak token. Returns a Promise<string> with the new token.
   */
  public static updateToken(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      KeycloakService.auth.authz
        .updateToken(30)
        .then(() => {
          resolve(KeycloakService.auth.authz.token as string);
        })
        .catch(() => {
          if (KeycloakService.updateRefreshTokenFunc) {
            KeycloakService.updateRefreshTokenFunc().subscribe(
              (x: any) => resolve(x),
              (_error: any) => {
                console.error('unable to refresh token with registered func');
                KeycloakService.logout();
                reject(false);
              },
            );
          } else {
            console.error('unable to refresh token');
            KeycloakService.logout();
            reject(false);
          }
        });
    });
  }

  public static registerLogoutFunc(func: any) {
    KeycloakService.logoutFuncs.push(func);
  }

  public static logout() {
    // cleanup before redirect
    for (const func of KeycloakService.logoutFuncs) {
      func();
    }
    // Use the build-time WEB_ENDPOINT constant. `window.__WL_CONFIG__` was never
    // a real runtime global: Vite's `define` only substitutes the bare
    // `__WL_CONFIG__` identifier (as in env.ts), not property accesses like
    // `window.__WL_CONFIG__`, so the latter stayed undefined in the bundle and
    // threw "Cannot read properties of undefined (reading 'WEB_ENDPOINT')".
    window.location.href = KeycloakService.auth.authz.createLogoutUrl({
      redirectUri: WEB_ENDPOINT,
    });
  }
}
